import React, { useState, useEffect, useCallback, useRef } from 'react';
import './MarketModal.css';
import { getRarityColor, getRarityClass, getRarityGlow } from '../utils/rarityUtils';
import ItemTooltipContent from './shared/ItemTooltipContent';
import TooltipBadge from './shared/TooltipBadge';
import { formatCurrency } from './shared/formatCompact';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// Local error boundary so market bugs don't crash the whole game page
class MarketErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) { console.error('[Market] Render error:', err, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="mkt-overlay" onClick={this.props.onClose}>
          <div className="mkt-modal" onClick={e => e.stopPropagation()} style={{ textAlign: 'center', padding: '40px' }}>
            <h3 style={{ color: '#e74c3c' }}>⚠️ Market Error</h3>
            <p style={{ color: '#ccc', margin: '16px 0' }}>Something went wrong loading the market. Please try again.</p>
            <button className="mkt-close-btn" onClick={this.props.onClose} style={{ padding: '8px 24px', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Category display info
const CATEGORIES = {
  all:        { label: 'All Items',    icon: '✨' },
  weapon:     { label: 'Weapons',      icon: '✨' },
  armor:      { label: 'Armor',        icon: '✨' },
  tome:       { label: 'Tomes',        icon: '✨' },
  scroll:     { label: 'Scrolls',      icon: '✨' },
  consumable: { label: 'Consumables',  icon: '✨' },
  backpack:   { label: 'Bags',         icon: '✨' },
  material:   { label: 'Materials',    icon: '✨' },
  other:      { label: 'Other',        icon: '✨' },
};

// Sort options
const SORT_OPTIONS = [
  { value: 'ending_soon', label: '✨ Ending Soon' },
  { value: 'newest',      label: '✨ Newest' },
  { value: 'price_asc',   label: '💰 Price: Low  High' },
  { value: 'price_desc',  label: '💰 Price: High  Low' },
  { value: 'name_asc',    label: '✨ Name: A  Z' },
];

// Duration options for selling
const DURATIONS = [
  { value: 1800,   label: '30 Minutes' },
  { value: 3600,   label: '1 Hour' },
  { value: 10800,  label: '3 Hours' },
  { value: 21600,  label: '6 Hours' },
  { value: 43200,  label: '12 Hours' },
  { value: 86400,  label: '1 Day' },
  { value: 259200, label: '3 Days' },
  { value: 604800, label: '7 Days' },
];

// Item type color classes (reuse shop palette)
const typeColorClass = (type) => {
  if (!type) return '';
  if (type === 'weapon') return 'mkt-type-weapon';
  if (['breastplate','helm','gloves','grieves','pauldrons','belt','armor','ring','shield'].includes(type)) return 'mkt-type-armor';
  if (type === 'tome') return 'mkt-type-tome';
  if (type === 'scroll') return 'mkt-type-scroll';
  if (type === 'restore') return 'mkt-type-consumable';
  if (type === 'backpack') return 'mkt-type-backpack';
  if (['gem','parts'].includes(type)) return 'mkt-type-material';
  return 'mkt-type-other';
};

// Format a magic enchantment entry for display (handles all shapes from TreasureGenerator/CraftingEngine)
function formatMagicEntry(m) {
  if (!m || typeof m !== 'object') return String(m || '');
  if (m.type === 'ablup') return `✨ +${m.bonus || m.mod || '?'} ${String(m.abl || '').toUpperCase()}`;
  if (m.type === 'elem') return `🛡️ ${String(m.element || '').charAt(0).toUpperCase() + String(m.element || '').slice(1)} Resist ${Math.round((m.resist || 0) * 100)}%`;
  if (m.type === 'hpregen') return `✨ +${m.mod || '?'} HP Regen`;
  if (m.type === 'mpregen') return `✨ +${m.mod || '?'} MP Regen`;
  if (m.type === 'hpup') return `✨ +${m.mod || '?'} Max HP`;
  if (m.type === 'mpup') return `✨ +${m.mod || '?'} Max MP`;
  if (m.type === 'sr') return `✨ +${m.mod || '?'} Spell Resist`;
  if (m.type === 'dr') return `✨ +${m.mod || '?'} Damage Resist`;
  // Standard stat enchants (str, agl, stam, int, wis, cha, lck)
  return `✨ +${m.mod || m.bonus || '?'} ${String(m.type || '').toUpperCase()}`;
}

// Safely render a value as text (prevents Objects-are-not-valid-as-React-child crashes)
function safeText(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

// ---- Time formatting helpers ----
function formatTimeRemaining(expiresAt) {
  const diff = new Date(expiresAt) - Date.now();
  if (diff <= 0) return 'Expired';
  const s = Math.floor(diff / 1000);
  if (s >= 86400) { const d = Math.floor(s / 86400); const h = Math.floor((s % 86400) / 3600); return `${d}d ${h}h`; }
  if (s >= 3600) { const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); return `${h}h ${m}m`; }
  if (s >= 60) { const m = Math.floor(s / 60); const sec = s % 60; return `${m}m ${sec}s`; }
  return `${s}s`;
}

function isEndingSoon(expiresAt) {
  return (new Date(expiresAt) - Date.now()) < 600000; // < 10 min
}


const MarketModal = ({ character, onClose, onCharacterUpdate }) => {
  // ---- State ----
  const [view, setView] = useState('browse'); // browse | sell | my-listings | item-detail
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: 'info' });
  const [playerGold, setPlayerGold] = useState(character?.gold || 0);
  const [playerInventory, setPlayerInventory] = useState([]);

  // Browse state
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState(''); // Debounced search value used for API calls
  const [sort, setSort] = useState('ending_soon');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [totalListings, setTotalListings] = useState(0);

  // Detail view / bidding
  const [selectedListing, setSelectedListing] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [confirmBid, setConfirmBid] = useState(false);

  // Sell form
  const [sellItemIndex, setSellItemIndex] = useState(-1);
  const [sellQty, setSellQty] = useState(1);
  const [sellReserve, setSellReserve] = useState('');
  const [sellBuynow, setSellBuynow] = useState('');
  const [sellDuration, setSellDuration] = useState(86400);
  const [sellComment, setSellComment] = useState('');
  const [confirmSell, setConfirmSell] = useState(false);

  // My Listings
  const [myListings, setMyListings] = useState({ selling: [], bidding: [] });
  const [actionBusy, setActionBusy] = useState(false);

  // Timer
  const timerRef = useRef(null);
  const [, setTick] = useState(0); // force re-render for countdown

  // ---- Sync from props ----
  useEffect(() => {
    if (character) {
      setPlayerGold(character.gold || 0);
      try {
        const inv = typeof character.inventory === 'string'
          ? JSON.parse(character.inventory)
          : (character.inventory || []);
        setPlayerInventory(inv);
      } catch { setPlayerInventory([]); }
    }
  }, [character]);

  // ---- Countdown timer (only runs when viewing buy tab where expiry times are shown) ----
  useEffect(() => {
    if (view !== 'browse') return;
    timerRef.current = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [view]);

  // ---- API helpers ----
  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  }), []);

  const showMsg = (text, type = 'info') => setMessage({ text, type });

  // ---- Fetch listings ----
  const fetchListings = useCallback(async (overridePage) => {
    try {
      setLoading(true);
      const p = overridePage || page;
      const params = new URLSearchParams({
        page: p,
        pageSize: 50,
        sort,
        ...(category !== 'all' && { category }),
        ...(appliedSearch && { search: appliedSearch }),
      });
      const resp = await fetch(`${API_BASE_URL}/api/market/listings?${params}`, { headers: authHeaders() });
      if (!resp.ok) throw new Error('Failed to fetch');
      const data = await resp.json();
      setListings(data.listings);
      setTotalPages(data.totalPages);
      setCategoryCounts(data.categoryCounts);
      setTotalListings(data.totalListings);
    } catch (err) {
      showMsg('Error loading market: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [page, sort, category, appliedSearch, authHeaders]);

  // Fetch on mount and when filters change
  useEffect(() => {
    if (view === 'browse') {
      fetchListings();
    }
  }, [view, category, sort, page, fetchListings]);

  // Debounced search
  const searchTimeoutRef = useRef(null);
  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setAppliedSearch(val);
      setPage(1);
    }, 400);
  };

  // ---- Fetch my listings ----
  const fetchMyListings = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await fetch(`${API_BASE_URL}/api/market/my-listings`, { headers: authHeaders() });
      if (!resp.ok) throw new Error('Failed to fetch');
      const data = await resp.json();
      setMyListings(data);
    } catch (err) {
      showMsg('Error loading your listings: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (view === 'my-listings') fetchMyListings();
  }, [view, fetchMyListings]);

  // ---- Actions ----

  const handleBid = async () => {
    if (actionBusy) return;
    const amount = parseInt(bidAmount);
    if (!amount || amount < 1) { showMsg('Enter a valid bid amount.', 'error'); return; }
    if (amount <= (selectedListing?.currentBid || 0)) {
      showMsg(`Bid must be higher than ${selectedListing.currentBid} gold.`, 'error'); return;
    }
    if (amount > playerGold) { showMsg(`Not enough gold! You have ${playerGold}.`, 'error'); return; }

    if (!confirmBid) { setConfirmBid(true); return; }

    setActionBusy(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/market/bid`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ listingId: selectedListing.id, amount }),
      });
      const data = await resp.json();
      if (data.success) {
        showMsg(data.message, 'success');
        setPlayerGold(data.gold);
        setConfirmBid(false);
        setBidAmount('');
        if (data.buyNow) {
          setSelectedListing(null);
          setView('browse');
          fetchListings();
        } else {
          // Refresh the listing detail
          fetchListings();
          setSelectedListing(prev => prev ? { ...prev, currentBid: amount, bidderName: character.name } : null);
        }
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        showMsg(data.error || 'Bid failed', 'error');
        setConfirmBid(false);
      }
    } catch (err) {
      showMsg('Error placing bid: ' + err.message, 'error');
      setConfirmBid(false);
    } finally {
      setActionBusy(false);
    }
  };

  const handleBuyNow = async (listing) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/market/bid`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ listingId: listing.id, amount: listing.buynowPrice }),
      });
      const data = await resp.json();
      if (data.success) {
        showMsg(data.message, 'success');
        setPlayerGold(data.gold);
        setSelectedListing(null);
        fetchListings();
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        showMsg(data.error || 'Purchase failed', 'error');
      }
    } catch (err) {
      showMsg('Error buying: ' + err.message, 'error');
    } finally {
      setActionBusy(false);
    }
  };

  const handleSell = async () => {
    if (sellItemIndex < 0) { showMsg('Select an item to sell.', 'error'); return; }

    const reserve = parseInt(sellReserve) || 0;
    const buynow = parseInt(sellBuynow) || 0;

    if (buynow > 0 && reserve > 0 && buynow < reserve) {
      showMsg('Buy-now price cannot be lower than reserve price.', 'error'); return;
    }

    if (!confirmSell) { setConfirmSell(true); return; }

    try {
      const resp = await fetch(`${API_BASE_URL}/api/market/sell`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          itemIndex: sellItemIndex,
          quantity: sellQty,
          reserve,
          buynow,
          duration: sellDuration,
          comment: sellComment.substring(0, 100),
        }),
      });
      const data = await resp.json();
      if (data.success) {
        showMsg(data.message, 'success');
        setPlayerInventory(data.inventory);
        resetSellForm();
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        showMsg(data.error || 'Listing failed', 'error');
        setConfirmSell(false);
      }
    } catch (err) {
      showMsg('Error listing item: ' + err.message, 'error');
      setConfirmSell(false);
    }
  };

  const handleCancel = async (listingId) => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/market/cancel`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ listingId }),
      });
      const data = await resp.json();
      if (data.success) {
        showMsg(data.message, 'success');
        setPlayerInventory(data.inventory);
        fetchMyListings();
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        showMsg(data.error || 'Cancel failed', 'error');
      }
    } catch (err) {
      showMsg('Error cancelling: ' + err.message, 'error');
    }
  };

  const handleSellNow = async (listingId) => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/market/sellnow`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ listingId }),
      });
      const data = await resp.json();
      if (data.success) {
        showMsg(data.message, 'success');
        setPlayerGold(data.gold);
        fetchMyListings();
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        showMsg(data.error || 'Sell now failed', 'error');
      }
    } catch (err) {
      showMsg('Error selling now: ' + err.message, 'error');
    }
  };

  const resetSellForm = () => {
    setSellItemIndex(-1);
    setSellQty(1);
    setSellReserve('');
    setSellBuynow('');
    setSellDuration(86400);
    setSellComment('');
    setConfirmSell(false);
  };

  // ---- Keyboard ----
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (selectedListing) { setSelectedListing(null); setConfirmBid(false); }
      else if (confirmSell) { setConfirmSell(false); }
      else onClose();
    }
  };

  // ---- Sell price helper (for reference on the sell form) ----
  const getShopSellPrice = (item) => Math.max(1, Math.floor((item.cost || 1) / 4));

  // ---- Render ----
  return (
    <div className="mkt-overlay" onClick={onClose} onKeyDown={handleKeyDown} tabIndex={-1}>
      <div className="mkt-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="mkt-header">
          <h2>The Market</h2>
          <div className="mkt-header-right">
            <div className="mkt-gold">{formatCurrency(playerGold || 0)}</div>
            <button className="mkt-close-btn" onClick={onClose} title="Close (Esc)">✖</button>
          </div>
        </div>

        {/* Navigation tabs */}
        <div className="mkt-tabs">
          <button className={`mkt-tab ${view === 'browse' ? 'active' : ''}`} onClick={() => { setView('browse'); setSelectedListing(null); }}>
             Browse ({totalListings})
          </button>
          <button className={`mkt-tab ${view === 'sell' ? 'active' : ''}`} onClick={() => { setView('sell'); resetSellForm(); }}>
            💰 Sell Item
          </button>
          <button className={`mkt-tab ${view === 'my-listings' ? 'active' : ''}`} onClick={() => setView('my-listings')}>
             My Activity
          </button>
        </div>

        {/* Content */}
        <div className="mkt-content">

          {/* BROWSE VIEW */}
          {view === 'browse' && !selectedListing && (
            <>
              {/* Filters bar */}
              <div className="mkt-filters">
                <div className="mkt-category-tabs">
                  {Object.entries(CATEGORIES).map(([key, { label, icon }]) => {
                    const count = key === 'all' ? totalListings : (categoryCounts[key] || 0);
                    return (
                      <button
                        key={key}
                        className={`mkt-cat-tab ${category === key ? 'active' : ''}`}
                        onClick={() => { setCategory(key); setPage(1); }}
                      >
                        {icon} {label} <span className="mkt-cat-count">({count})</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mkt-filter-row">
                  <input
                    type="text"
                    className="mkt-search"
                    placeholder="🔍 Search items by name..."
                    value={search}
                    onChange={e => handleSearchChange(e.target.value)}
                  />
                  <select className="mkt-sort" value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}>
                    {SORT_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Listings table */}
              {loading ? (
                <div className="mkt-empty">Loading listings...</div>
              ) : listings.length === 0 ? (
                <div className="mkt-empty">
                  No items found.{search ? ' Try a different search.' : ' Be the first to list something!'}
                </div>
              ) : (
                <>
                  <div className="mkt-table">
                    <div className="mkt-table-header">
                      <span className="mkt-col-time">Time Left</span>
                      <span className="mkt-col-item">Item</span>
                      <span className="mkt-col-qty">Qty</span>
                      <span className="mkt-col-bid">High Bid</span>
                      <span className="mkt-col-buynow">Buy Now</span>
                      <span className="mkt-col-seller">Seller</span>
                      <span className="mkt-col-action"></span>
                    </div>
                    {listings.map(listing => (
                      <div
                        key={listing.id}
                        className={`mkt-table-row ${isEndingSoon(listing.expiresAt) ? 'ending-soon' : ''}`}
                        onClick={() => { setSelectedListing(listing); setConfirmBid(false); setBidAmount(''); }}
                      >
                        <span className={`mkt-col-time ${isEndingSoon(listing.expiresAt) ? 'time-urgent' : ''}`}>
                          {formatTimeRemaining(listing.expiresAt)}
                        </span>
                        <span className={`mkt-col-item ${getRarityClass(listing.itemData)}`} style={{ color: getRarityColor(listing.itemData), textShadow: getRarityGlow(listing.itemData) }}>
                          {listing.itemName}
                          {listing.itemInfo?.level > 1 && <span className="mkt-item-level">Lv{listing.itemInfo.level}</span>}
                        </span>
                        <span className="mkt-col-qty">{listing.quantity > 1 ? listing.quantity : ''}</span>
                        <span className="mkt-col-bid">
                          {(listing.currentBid || 0) > 0 ? Number(listing.currentBid).toLocaleString() : ''}
                          {listing.reserveMet && listing.currentBid > 0 && <span className="mkt-reserve-met" title="Reserve met"></span>}
                        </span>
                        <span className="mkt-col-buynow">
                          {(listing.buynowPrice || 0) > 0 ? Number(listing.buynowPrice).toLocaleString() : ''}
                        </span>
                        <span className="mkt-col-seller">{safeText(listing.sellerName)}</span>
                        <span className="mkt-col-action">
                          <button className="mkt-view-btn" onClick={(e) => { e.stopPropagation(); setSelectedListing(listing); setConfirmBid(false); setBidAmount(''); }}>
                            View
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mkt-pagination">
                      <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                      <span>Page {page} of {totalPages}</span>
                      <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ITEM DETAIL VIEW */}
          {view === 'browse' && selectedListing && (
            <div className="mkt-detail">
              <button className="mkt-back-btn" onClick={() => { setSelectedListing(null); setConfirmBid(false); }}>⬅️ Back to Listings</button>

              <div className="mkt-detail-card">
                <div className="mkt-detail-header">
                  <h3 className={getRarityClass(selectedListing.itemData)} style={{ color: getRarityColor(selectedListing.itemData), textShadow: getRarityGlow(selectedListing.itemData) }}>
                    {selectedListing.itemName}
                    {selectedListing.quantity > 1 && <span className="mkt-detail-qty"> (x{selectedListing.quantity})</span>}
                  </h3>
                  <span className={`mkt-detail-timer ${isEndingSoon(selectedListing.expiresAt) ? 'time-urgent' : ''}`}>
                     {formatTimeRemaining(selectedListing.expiresAt)}
                  </span>
                </div>

                {/* Item description */}
                <div className="mkt-detail-desc">
                  <ItemTooltipContent item={selectedListing.itemData || selectedListing.itemInfo || {}} canLearnSpells={false} />
                </div>

                {selectedListing.comment && (
                  <div className="mkt-detail-comment">"{selectedListing.comment}"</div>
                )}

                {/* Auction info */}
                <div className="mkt-detail-info">
                  <div className="mkt-info-row">
                    <span>Seller</span><span>{safeText(selectedListing.sellerName)}</span>
                  </div>
                  <div className="mkt-info-row">
                    <span>Shop Value</span>
                    <span>{selectedListing.itemInfo?.cost ? `${Number(selectedListing.itemInfo.cost).toLocaleString()} gp` : ''}</span>
                  </div>
                  <div className="mkt-info-row">
                    <span>High Bid</span>
                    <span className="mkt-bid-value">
                      {(selectedListing.currentBid || 0) > 0
                        ? `${Number(selectedListing.currentBid).toLocaleString()} gp (${safeText(selectedListing.bidderName)})`
                        : 'No bids yet'}
                    </span>
                  </div>
                  <div className="mkt-info-row">
                    <span>Reserve</span>
                    <span>
                      {(selectedListing.reservePrice || 0) > 0
                        ? (selectedListing.reserveMet ? '✨ Met' : '✨ Not met')
                        : 'None'}
                    </span>
                  </div>
                  <div className="mkt-info-row">
                    <span>Buy Now</span>
                    <span>{(selectedListing.buynowPrice || 0) > 0 ? `${Number(selectedListing.buynowPrice).toLocaleString()} gp` : 'N/A'}</span>
                  </div>
                </div>

                {/* Actions */}
                {selectedListing.sellerId !== (character?.id) && (
                  <div className="mkt-detail-actions">
                    {/* Bid */}
                    <div className="mkt-bid-form">
                      <label>Place Bid:</label>
                      <input
                        type="number"
                        className="mkt-bid-input"
                        placeholder={`Min: ${(selectedListing.currentBid || 0) + 1}`}
                        value={bidAmount}
                        onChange={e => { setBidAmount(e.target.value); setConfirmBid(false); }}
                        min={(selectedListing.currentBid || 0) + 1}
                      />
                      <span className="mkt-bid-gold-hint">Your gold: {formatCurrency(playerGold || 0)}</span>
                      {confirmBid ? (
                        <div className="mkt-confirm-row">
                          <span className="mkt-confirm-text">
                            Bid <strong>{parseInt(bidAmount).toLocaleString()} gp</strong> on {selectedListing.itemName}? Gold will be deducted immediately.
                          </span>
                          <button className="mkt-confirm-btn" onClick={handleBid}>Confirm Bid</button>
                          <button className="mkt-cancel-btn" onClick={() => setConfirmBid(false)}>❌ Cancel</button>
                        </div>
                      ) : (
                        <button
                          className="mkt-bid-btn"
                          onClick={handleBid}
                          disabled={!bidAmount || parseInt(bidAmount) <= (selectedListing.currentBid || 0) || parseInt(bidAmount) > playerGold}
                        >
                          Place Bid
                        </button>
                      )}
                    </div>

                    {/* Buy Now */}
                    {selectedListing.buynowPrice > 0 && (
                      <button
                        className="mkt-buynow-btn"
                        onClick={() => {
                          if (playerGold < selectedListing.buynowPrice) {
                            showMsg(`Not enough gold! Need ${selectedListing.buynowPrice}, have ${playerGold}.`, 'error');
                          } else {
                            handleBuyNow(selectedListing);
                          }
                        }}
                        disabled={playerGold < selectedListing.buynowPrice}
                      >
                        🛒 Buy Now for {Number(selectedListing.buynowPrice || 0).toLocaleString()} gp
                      </button>
                    )}
                  </div>
                )}

                {selectedListing.sellerId === (character?.id) && (
                  <div className="mkt-detail-owner-note">
                    This is your listing. Manage it from the "My Activity" tab.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SELL VIEW */}
          {view === 'sell' && (
            <div className="mkt-sell">
              <div className="mkt-sell-notice">
                 The Market charges a <strong>2% commission</strong> on all completed sales.
                <TooltipBadge id="market.commission" />
              </div>

              {!confirmSell ? (
                <div className="mkt-sell-form">
                  {/* Item selector */}
                  <div className="mkt-form-group">
                    <label>Select Item to Sell:</label>
                    <select
                      className="mkt-select"
                      value={sellItemIndex}
                      onChange={e => { setSellItemIndex(parseInt(e.target.value)); setSellQty(1); }}
                    >
                      <option value={-1}>Choose an item </option>
                      {playerInventory.map((item, idx) => (
                        <option key={idx} value={idx}>
                          {item.name}{(item.quantity || 1) > 1 ? ` (x${item.quantity})` : ''}  Shop sell: {getShopSellPrice(item)} gp
                        </option>
                      ))}
                    </select>
                  </div>

                  {sellItemIndex >= 0 && playerInventory[sellItemIndex] && (
                    <>
                      {/* Item preview */}
                      <div className="mkt-sell-preview">
                        <span className={getRarityClass(playerInventory[sellItemIndex])} style={{ color: getRarityColor(playerInventory[sellItemIndex]) }}>
                          {playerInventory[sellItemIndex].name}
                        </span>
                        {playerInventory[sellItemIndex].description && (
                          <span className="mkt-sell-desc">  {playerInventory[sellItemIndex].description}</span>
                        )}
                      </div>

                      {/* Quantity */}
                      {(playerInventory[sellItemIndex].quantity || 1) > 1 && (
                        <div className="mkt-form-group">
                          <label>Quantity (have {playerInventory[sellItemIndex].quantity}):</label>
                          <div className="mkt-qty-row">
                            <input
                              type="number"
                              className="mkt-input-sm"
                              min="1"
                              max={playerInventory[sellItemIndex].quantity}
                              value={sellQty}
                              onChange={e => setSellQty(Math.max(1, Math.min(playerInventory[sellItemIndex].quantity, parseInt(e.target.value) || 1)))}
                            />
                            <button className="mkt-qty-btn" onClick={() => setSellQty(1)}>1</button>
                            <button className="mkt-qty-btn" onClick={() => setSellQty(Math.min(5, playerInventory[sellItemIndex].quantity || 1))}>5</button>
                            <button className="mkt-qty-btn" onClick={() => setSellQty(Math.min(10, playerInventory[sellItemIndex].quantity || 1))}>10</button>
                            <button className="mkt-qty-btn" onClick={() => setSellQty(playerInventory[sellItemIndex].quantity || 1)}>All</button>
                          </div>
                        </div>
                      )}

                      {/* Reserve price */}
                      <div className="mkt-form-group">
                        <label>Reserve Price <span className="mkt-hint">(min price to accept, 0 = none)</span>:</label>
                        <input
                          type="number"
                          className="mkt-input-sm"
                          placeholder="0"
                          min="0"
                          value={sellReserve}
                          onChange={e => setSellReserve(e.target.value)}
                        />
                      </div>

                      {/* Buy now price */}
                      <div className="mkt-form-group">
                        <label>Buy Now Price <span className="mkt-hint">(instant buy price, 0 = none)</span>:</label>
                        <input
                          type="number"
                          className="mkt-input-sm"
                          placeholder="0"
                          min="0"
                          value={sellBuynow}
                          onChange={e => setSellBuynow(e.target.value)}
                        />
                      </div>

                      {/* Duration */}
                      <div className="mkt-form-group">
                        <label>Auction Duration:</label>
                        <select className="mkt-select" value={sellDuration} onChange={e => setSellDuration(parseInt(e.target.value))}>
                          {DURATIONS.map(d => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Comment */}
                      <div className="mkt-form-group">
                        <label>Comment <span className="mkt-hint">(max 100 chars)</span>:</label>
                        <input
                          type="text"
                          className="mkt-input"
                          maxLength={100}
                          placeholder="Optional message for buyers..."
                          value={sellComment}
                          onChange={e => setSellComment(e.target.value)}
                        />
                      </div>

                      <button className="mkt-sell-btn" onClick={handleSell}>
                        📋 List on Market
                      </button>
                    </>
                  )}
                </div>
              ) : (
                /* Confirmation screen */
                <div className="mkt-sell-confirm">
                  <h4>Confirm Listing</h4>
                  <div className="mkt-confirm-details">
                    <p><strong>Item:</strong> {playerInventory[sellItemIndex]?.name}</p>
                    <p><strong>Quantity:</strong> {sellQty}</p>
                    {parseInt(sellReserve) > 0 && <p><strong>Reserve Price:</strong> {parseInt(sellReserve).toLocaleString()} gp</p>}
                    {parseInt(sellBuynow) > 0 && <p><strong>Buy Now Price:</strong> {parseInt(sellBuynow).toLocaleString()} gp</p>}
                    <p><strong>Duration:</strong> {DURATIONS.find(d => d.value === sellDuration)?.label}</p>
                    {sellComment && <p><strong>Comment:</strong> "{sellComment}"</p>}
                  </div>
                  <div className="mkt-confirm-buttons">
                    <button className="mkt-confirm-btn" onClick={handleSell}>Yes, List It</button>
                    <button className="mkt-cancel-btn" onClick={() => setConfirmSell(false)}>Go Back</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* MY ACTIVITY VIEW */}
          {view === 'my-listings' && (
            <div className="mkt-my-activity">
              {loading ? (
                <div className="mkt-empty">Loading...</div>
              ) : (
                <>
                  {/* My Sell Listings */}
                  <h4 className="mkt-section-title">Items I'm Selling ({myListings.selling.length})</h4>
                  {myListings.selling.length === 0 ? (
                    <div className="mkt-empty-sm">You have no active listings.</div>
                  ) : (
                    <div className="mkt-my-list">
                      {myListings.selling.map(listing => (
                        <div key={listing.id} className="mkt-my-row">
                          <div className="mkt-my-info">
                            <span className={`mkt-my-name ${typeColorClass(listing.itemData?.type)}`}>
                              {listing.itemName}
                              {listing.quantity > 1 && ` (x${listing.quantity})`}
                            </span>
                            <span className="mkt-my-timer">{formatTimeRemaining(listing.expiresAt)}</span>
                          </div>
                          <div className="mkt-my-details">
                            <span>Bid: {(listing.currentBid || 0) > 0 ? `${Number(listing.currentBid).toLocaleString()} gp (${safeText(listing.bidderName)})` : 'No bids'}</span>
                            {(listing.buynowPrice || 0) > 0 && <span>Buy Now: {Number(listing.buynowPrice).toLocaleString()} gp</span>}
                            <span>Reserve: {(listing.reservePrice || 0) > 0 ? (listing.reserveMet ? '✨ Met' : `${listing.reservePrice} gp`) : 'None'}</span>
                          </div>
                          <div className="mkt-my-actions">
                            {listing.currentBid > 0 ? (
                              <button className="mkt-sellnow-btn" onClick={() => handleSellNow(listing.id)}>
                                💰 Sell Now ({listing.currentBid} gp)
                              </button>
                            ) : (
                              <button className="mkt-cancel-listing-btn" onClick={() => handleCancel(listing.id)}>
                                ❌ Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* My Active Bids */}
                  <h4 className="mkt-section-title" style={{ marginTop: '20px' }}>Items I'm Bidding On ({myListings.bidding.length})</h4>
                  {myListings.bidding.length === 0 ? (
                    <div className="mkt-empty-sm">You have no active bids.</div>
                  ) : (
                    <div className="mkt-my-list">
                      {myListings.bidding.map(listing => (
                        <div key={listing.id} className="mkt-my-row">
                          <div className="mkt-my-info">
                            <span className={`mkt-my-name ${typeColorClass(listing.itemData?.type)}`}>
                              {listing.itemName}
                              {listing.quantity > 1 && ` (x${listing.quantity})`}
                            </span>
                            <span className="mkt-my-timer">{formatTimeRemaining(listing.expiresAt)}</span>
                          </div>
                          <div className="mkt-my-details">
                            <span>Your Bid: {Number(listing.currentBid || 0).toLocaleString()} gp</span>
                            <span>Seller: {safeText(listing.sellerName)}</span>
                            <span>Reserve: {listing.reserveMet ? '✨ Met' : '✨ Not met'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

        </div>

        {/* Message bar */}
        <div className={`mkt-message ${message.type}`}>
          {message.text}
        </div>
      </div>
    </div>
  );
};

// Wrap in local error boundary so market issues don't crash the entire game
const MarketModalWrapper = (props) => (
  <MarketErrorBoundary onClose={props.onClose}>
    <MarketModal {...props} />
  </MarketErrorBoundary>
);

export default MarketModalWrapper;
